import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { notificationsService } from '@/services/notifications';
import type {
  ListNotificationsResponse,
  NotificationStatusFilter,
  NotificationType,
  UnreadNotificationsCount,
} from '@/services/notifications/types';

export const NOTIFICATIONS_KEY = 'notifications';
export const NOTIFICATIONS_UNREAD_COUNT_KEY = [
  NOTIFICATIONS_KEY,
  'unread-count',
] as const;
export const NOTIFICATIONS_TYPES_KEY = [NOTIFICATIONS_KEY, 'types'] as const;

/** O polling do sino é de minuto em minuto; nada mais agressivo que isso. */
const UNREAD_COUNT_POLL_INTERVAL_MS = 60_000;
const UNREAD_COUNT_STALE_TIME_MS = 30_000;
/** O catálogo de tipos muda com deploy do back, não durante a sessão. */
const TYPES_STALE_TIME_MS = 60 * 60 * 1000;

export type UseNotificationsFilters = {
  type?: string;
  status?: NotificationStatusFilter;
  /**
   * Corretora cujo feed será lido. Preenchida apenas nos perfis da seguradora,
   * a partir do seletor do header; nos perfis de corretora o back resolve o
   * escopo pelo token e ignora este valor.
   */
  partyCode?: string;
};

/**
 * Contagem de não lidas do sino.
 *
 * É a ÚNICA query que faz polling: a lista só é buscada quando o painel abre.
 * O `refetchIntervalInBackground` fica no padrão (`false`), então uma aba
 * escondida não gasta chamada.
 */
export function useUnreadNotificationsCount(
  enabled: boolean,
  partyCode?: string
) {
  return useQuery<UnreadNotificationsCount, Error>({
    // A corretora entra na chave: trocar de corretora no header precisa buscar
    // uma contagem nova, não reaproveitar a da anterior.
    queryKey: [...NOTIFICATIONS_UNREAD_COUNT_KEY, partyCode ?? null],
    queryFn: async () => {
      const result = await notificationsService.getUnreadCount(partyCode);
      if (!result.success || !result.data) {
        throw new Error(
          result.error ?? 'Não foi possível carregar as notificações.'
        );
      }
      return result.data;
    },
    enabled,
    refetchInterval: UNREAD_COUNT_POLL_INTERVAL_MS,
    staleTime: UNREAD_COUNT_STALE_TIME_MS,
    refetchOnWindowFocus: true,
  });
}

/**
 * Feed paginado por cursor.
 *
 * O back pode devolver uma página curta com `nextCursor` preenchido — o filtro
 * de tipo/status roda depois do limite do DynamoDB —, então quem decide se há
 * mais é sempre o cursor, nunca a quantidade de itens da página.
 */
export function useNotifications(
  filters: UseNotificationsFilters = {},
  options: { enabled?: boolean; limit?: number } = {}
) {
  return useInfiniteQuery({
    queryKey: [NOTIFICATIONS_KEY, 'list', filters, options.limit ?? null],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const result = await notificationsService.list({
        limit: options.limit,
        cursor: pageParam,
        type: filters.type || undefined,
        status: filters.status,
        partyCode: filters.partyCode,
      });
      if (!result.success || !result.data) {
        throw new Error(
          result.error ?? 'Não foi possível carregar as notificações.'
        );
      }
      return result.data;
    },
    getNextPageParam: (lastPage: ListNotificationsResponse) =>
      lastPage.nextCursor ?? undefined,
    enabled: options.enabled !== false,
    staleTime: UNREAD_COUNT_STALE_TIME_MS,
  });
}

/** Catálogo de tipos — alimenta o filtro da tela sem lista literal no front. */
export function useNotificationTypes(enabled = true) {
  return useQuery<NotificationType[], Error>({
    queryKey: NOTIFICATIONS_TYPES_KEY,
    queryFn: async () => {
      const result = await notificationsService.listTypes();
      if (!result.success || !result.data) {
        throw new Error(
          result.error ?? 'Não foi possível carregar os tipos de notificação.'
        );
      }
      return result.data.items;
    },
    enabled,
    staleTime: TYPES_STALE_TIME_MS,
  });
}

export function useMarkNotificationAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const result = await notificationsService.markAsRead(notificationId);
      if (!result.success) {
        throw new Error(
          result.error ?? 'Não foi possível marcar a notificação como lida.'
        );
      }
    },
    onSuccess: () => invalidateNotifications(queryClient),
  });
}

export function useMarkAllNotificationsAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const result = await notificationsService.markAllAsRead();
      if (!result.success || !result.data) {
        throw new Error(
          result.error ?? 'Não foi possível marcar as notificações como lidas.'
        );
      }
      return result.data;
    },
    onSuccess: () => invalidateNotifications(queryClient),
  });
}

function invalidateNotifications(
  queryClient: ReturnType<typeof useQueryClient>
): void {
  // Uma chave só: a contagem e as listas vivem sob `notifications`.
  void queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY] });
}
